/* eslint-disable max-lines, max-params */
import {Lang} from 'yougile-lang'
import {ArrUtil, logger, Util} from 'yougile-util'
import {
  CustomStickerType,
  customStickerTypes,
  DataObjectId,
  DeadlineStickerValue,
  GenericStickerState,
  Hub,
  HubData,
  HubLike,
  JSONLikeValue,
  SandboxTree,
  SprintStickerState,
  stickerDataFromTag,
  StickerIcon,
  stickerIconProperty,
  stickerReadonlyProperty,
  StickerStateId,
  StickerType,
  stickerTypeProperty,
  stickerTypes,
  stickerTypesWithoutSettings,
  StringStickerState,
  systemStickerIconsMap,
  SystemStickerType,
  systemStickerTypes,
  Tag,
  TimerStickerValue,
  TimetrackerValue,
  User
} from 'yougile-model'
import {Items} from '@api/model/items'
import {Stickers} from '@api/model/stickers'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {AnyColor} from '@components/common/base/design-system'
import {HelpKey} from '@components/common/base/help/types'
import {CustomIcons} from '@components/common/icon-tabler/custom-icons-16'
import {GanttDraft} from '@extensions/gantt-draft'
import {NumericCtl} from '@extensions/sticker/controllers/numeric-ctl'
import {TextCtl} from '@extensions/sticker/controllers/text-ctl'
import {SubtaskUtil} from '@extensions/subtask-util'
import {globals} from '../globals-base'
import {Task} from '../task'
import {BoardFilters} from './board-filters'
import {AStickerCtl} from './controllers/a-sticker-ctl'
import {ASystemStickerCtl} from './controllers/a-system-sticker-ctl'
import {DeadlineCtl} from './controllers/deadline-ctl'
import {RepeatCtl} from './controllers/repeat-ctl'
import {SprintCtl} from './controllers/sprint-ctl'
import {StopwatchCtl} from './controllers/stopwatch-ctl'
import {StringCtl} from './controllers/string-ctl'
import {TimerCtl} from './controllers/timer-ctl'
import {TimetrackingCtl} from './controllers/timetracking-ctl'
import {UserCtl} from './controllers/user-ctl'
import {StickerContainerKey, StickerHub} from './views-web/a-sticker-view'

/**
 * Stickers are tags inside /<company hub>/.stickers/ folder.
 * Each sticker type has a corresponding sticker controller class,
 * that controls its behavior and appearance
 * (that is defined inside sticker/controllers package).
 *
 * Sticker can be pinned to a task with a specific value.
 * To represent this, hubs have a special field “stickers” (see Hub class).
 *
 * User can filter board with some sticker values. User can choose multiple
 * sticker values or some sort of condition on sticker values and apply such
 * a filter to a specific board. This filter is stored locally, so it is
 * persistent between multiple app reloads.
 */

export interface StickerColor {
  readonly main: string
  readonly border: string
  readonly background: string
  readonly text: string
}

export interface SystemCtlDict {
  readonly Timetracking: TimetrackingCtl
  readonly User: UserCtl
  readonly Deadline: DeadlineCtl
  readonly Timer: TimerCtl
  readonly Stopwatch: StopwatchCtl
  readonly Repeat: RepeatCtl
}

export const systemStickerCtl: SystemCtlDict & {
  readonly [name: string]: ASystemStickerCtl<unknown>
} = {
  User: new UserCtl(),
  Deadline: new DeadlineCtl(),
  Timer: new TimerCtl(),
  Stopwatch: new StopwatchCtl(),
  Timetracking: new TimetrackingCtl(),
  Repeat: new RepeatCtl()
}

export const stickerCtl: SystemCtlDict & {
  readonly [name: string]: AStickerCtl<unknown>
  readonly String: StringCtl
  readonly Sprint: SprintCtl
} = {
  ...systemStickerCtl,
  String: new StringCtl(),
  Sprint: new SprintCtl(),
  Text: new TextCtl(),
  Numeric: new NumericCtl()
}

/**
 * This class simplifies working with stickers allowing to do stuff with
 * stickers without knowing the details of their database representations:
 * manipulate stickers on tasks, create new stickers or edit/delete old ones.
 */
export class Sticker {
  private static defaultColor = '#666666'

  static createStickerByType(
    type: StickerType,
    box: SandboxTree
  ): Tag | undefined {
    let sticker

    switch (type) {
      case 'sprint':
        sticker = new SprintCtl()
        break
      case 'string':
        sticker = new StringCtl()
        break
      case 'text':
        sticker = new TextCtl()
        break
      case 'numeric':
        sticker = new NumericCtl()
        break
    }

    return sticker ? sticker?.make(box) : undefined
  }

  /**
   * Returns true if task addition can be proceeded.
   */
  static createTaskHook(task: Hub): boolean {
    return stickerCtl.Timetracking.requestPlan(task, task.location)
  }

  static completeTaskHook(
    task: Hub,
    container: StickerContainerKey
  ): Promise<boolean> {
    return stickerCtl.Timetracking.requestComplete(task, container)
  }

  static moveTaskHook(
    task: Hub,
    from: Hub,
    to: Hub,
    container: StickerContainerKey
  ): Promise<boolean> {
    return stickerCtl.Timetracking.handleMove(task, from, to, container)
  }

  private static pinEmptyHook(
    task: Hub,
    sticker: Tag,
    container: StickerContainerKey
  ): void {
    const controller = Sticker.getController(sticker)
    if (controller) {
      controller.onPinEmpty(task, sticker, container)
    }
  }

  static order(a: Tag, b: Tag): number {
    const typeOrd = (t: Tag): number => stickerTypes.indexOf(Sticker.getType(t))
    return typeOrd(a) - typeOrd(b) || a.timestamp - b.timestamp
  }

  static setStickerOrder(board: Hub, stickersIds: DataObjectId[]): void {
    board.data.stickersOrder = stickersIds
    board.commit()
  }

  static orderStickers(data: HubData, stickers: Tag[]): Tag[] {
    if (!data.stickersOrder) {
      return stickers.sort(Sticker.order)
    }
    const {stickersOrder} = data
    return stickers.sort(
      (a, b) => stickersOrder.indexOf(a.id) - stickersOrder.indexOf(b.id)
    )
  }

  static getType(sticker: Tag): StickerType {
    return sticker.properties[stickerTypeProperty] as StickerType
  }

  static getHelpKey(sticker: Tag): HelpKey {
    return `${Sticker.getType(sticker)}Sticker` as HelpKey
  }

  static isReadonly(sticker: Tag): boolean {
    return !!sticker.properties[stickerReadonlyProperty]
  }

  static init(box: SandboxTree): void {
    Object.keys(systemStickerCtl).forEach(k => systemStickerCtl[k].init(box))
    BoardFilters.load()
  }

  // TODO! deprecated.
  static getController(
    stickerOrType: Tag | StickerType
  ): AStickerCtl<unknown> | undefined {
    const type =
      typeof stickerOrType === 'string'
        ? stickerOrType
        : Sticker.getType(stickerOrType)

    return (
      Object.keys(stickerCtl)
        .map(k => stickerCtl[k])
        .find(c => c.type === type) || stickerCtl.String
    )
  }

  static getName(sticker: Tag): string {
    const controller = Sticker.getController(sticker)
    return controller ? controller.displayedTitle(sticker) : ''
  }

  static controllers(): Array<AStickerCtl<unknown>> {
    return Object.keys(stickerCtl)
      .map(k => stickerCtl[k])
      .filter(c => {
        if (c instanceof ASystemStickerCtl) {
          return !!c.getSticker(Sync.getCurrentSandbox())
        } else {
          return true
        }
      })
  }

  private static systemStickerCache: Map<DataObjectId, Map<string, Tag>> =
    new Map()
  static getByName(box: SandboxTree, title: string): Tag | undefined {
    const boxCache = Sticker.systemStickerCache.get(box.companyId)
    if (boxCache?.has(title)) {
      return boxCache.get(title)
    } else {
      const res = Sticker.list(box).find((s: Tag) => s.title === title)
      if (!boxCache) {
        Sticker.systemStickerCache.set(box.companyId, new Map())
      }
      if (res) {
        Sticker.systemStickerCache.get(box.companyId)?.set(title, res)
      }
      return res
    }
  }

  static checkedFixStatesOrder: {[key: DataObjectId]: boolean | undefined} = {}

  static fixStickerStatesOrder(
    boxOrBoard: SandboxTree | Hub | undefined
  ): void {
    if (!boxOrBoard) {
      return
    }
    const box = Sync.get(boxOrBoard)
    const container = Sticker.getStickersContainer(box)
    if (!container) {
      return
    }

    Sticker.list(boxOrBoard).forEach(sticker => {
      if (this.checkedFixStatesOrder[sticker.id]) {
        return
      }
      this.checkedFixStatesOrder[sticker.id] = true

      let needCommit = false

      const indexKeys = Object.keys(sticker.states.index)
      const order = sticker.states.order

      const keysToDelete = order.filter(stateId => !indexKeys.includes(stateId))
      if (keysToDelete.length) {
        needCommit = true
      }
      keysToDelete.forEach(key => {
        const idx = order.indexOf(key)
        if (idx !== -1) {
          order.splice(idx, 1)
        }
      })

      indexKeys.forEach(key => {
        if (!order.includes(key)) {
          order.push(key)
          needCommit = true
        }
      })

      if (order.length !== indexKeys.length) {
        logger.warn(
          `Not equal count of order and keys for sticker ${sticker.id}`
        )
      }

      if (needCommit) {
        sticker.commit()
      }
    })
  }

  static getById(
    boxOrBoard: SandboxTree | Hub,
    id: DataObjectId
  ): Tag | undefined {
    const box = Sync.get(boxOrBoard)
    return box.get(id) as Tag | undefined
  }

  /**
   * Returns all stickers added to the board’s sticker panel.
   * If no board is specified, returns all stickers.
   */
  static list(
    boxOrBoard: SandboxTree | Hub | undefined,
    opts?: {withSettings?: boolean}
  ): Tag[] {
    if (!boxOrBoard) {
      return []
    }
    const box = Sync.get(boxOrBoard)
    if (!box) {
      return []
    }
    const container = Sticker.getStickersContainer(box)
    if (!container) {
      return []
    }
    const all = box.getChildrenEntities(container.id) as Tag[]
    if (boxOrBoard instanceof Hub) {
      const board = boxOrBoard
      const filteredTag = all.filter(t =>
        opts?.withSettings
          ? Sticker.isInBoardOrSettings(board, t)
          : Sticker.isInBoard(board, t) && Sticker.check(t)
      )
      return Sticker.orderStickers(board.data, filteredTag)
    } else {
      return all
    }
  }

  static isInBoardOrSettings(board: Hub, sticker: Tag): boolean {
    return (
      Sticker.isInBoard(board, sticker) ||
      Sticker.isSettingsInBoard(board, sticker)
    )
  }

  static getStickersContainer(box: SandboxTree): Hub {
    const result = box.findHubByTitle('.stickers', box.companyId, true)
    if (!result) {
      throw new Error('.stickers container not found')
    }
    return result
  }

  static isInBoard(board: Hub, sticker: Tag): boolean {
    return !!board && sticker.id in board.stickers
  }

  static isActiveInBoard(board: Hub, sticker: Tag): boolean {
    return (
      Sticker.isInBoard(board, sticker) ||
      !Sticker.isSettingsInBoard(board, sticker)
    )
  }

  static isSettingsInBoard(board: Hub, sticker: Tag): boolean {
    return Boolean(
      board?.data.stickersSettings && sticker.id in board.data.stickersSettings
    )
  }

  static addToBoard(board: Hub, sticker: Tag, on = true): void {
    logger.log('Adding sticker', board.title, sticker.title)

    if (on) {
      board.stickers[sticker.id] = {}
    }

    board.data.stickersSettings ??= {}
    board.data.stickersSettings[sticker.id] = {}
    if (board.data.stickersOrder) {
      board.data.stickersOrder = [...board.data.stickersOrder, sticker.id]
    } else {
      board.data.stickersOrder = [sticker.id]
    }
    board.commit()
  }

  static toggleActiveInBoard(board: Hub, sticker: Tag): void {
    if (Sticker.isInBoard(board, sticker)) {
      Sticker.turnOffActiveInBoard(board, sticker)
    } else {
      Sticker.turnOnActiveInBoard(board, sticker)
      if (sticker.title === 'Timetracking') {
        Tracking.send(
          TrackingEvent.boardSettingsTurnOnTimetrackingStickerOnTheBoard
        )
      }
    }
  }

  static turnOffActiveInBoard(board: Hub, sticker: Tag): void {
    if (!Sticker.isSettingsInBoard(board, sticker)) {
      board.data.stickersSettings ??= {}
      board.data.stickersSettings[sticker.id] = {}
    }
    delete board.stickers[sticker.id]
    board.commit()
  }

  static turnOnActiveInBoard(board: Hub, sticker: Tag): void {
    board.stickers[sticker.id] = {}
    board.data.stickersSettings ??= {}
    board.data.stickersSettings[sticker.id] ??= {}
    const {stickersOrder = []} = board.data
    if (!stickersOrder.includes(sticker.id)) {
      board.data.stickersOrder = [...stickersOrder, sticker.id]
    }
    board.commit()
  }

  static toggleInBoard(board: Hub, sticker: Tag): void {
    if (Sticker.isInBoard(board, sticker)) {
      Sticker.removeFromBoard(board, sticker)
    } else {
      Sticker.addToBoard(board, sticker)
    }
  }

  static removeFromBoard(board: Hub, sticker: Tag): void {
    delete board.stickers[sticker.id]
    delete board.data.stickersSettings?.[sticker.id]
    if (board.data.stickersOrder) {
      board.data.stickersOrder = board.data.stickersOrder.filter(
        order => order !== sticker.id
      )
    }
    board.commit()
  }

  static create(
    box: SandboxTree,
    title: string,
    type: StickerType,
    icon?: StickerIcon
  ): Tag | null {
    const controller = Sticker.getController(type)
    if (!controller) {
      return null
    }
    const sticker = controller.make(box)
    sticker.title = title
    Sticker.add(box, sticker)
    Sticker.setIcon(sticker, icon || '')
    return sticker
  }

  static add(box: SandboxTree, newSticker: Tag): void {
    const container = Sticker.getStickersContainer(box)
    box.add(newSticker, container.id, true, false)
  }

  static remove(sticker: Tag): void {
    Sync.get(sticker).remove(sticker)
  }

  static isSystem(sticker: Tag): boolean {
    const stickerType = this.getType(sticker)
    return systemStickerTypes.includes(stickerType as SystemStickerType)
  }

  static isCustom(sticker: Tag): boolean {
    const stickerType = this.getType(sticker)
    return customStickerTypes.includes(stickerType as CustomStickerType)
  }

  static hasSettings(sticker: Tag): boolean {
    const stickerType = this.getType(sticker)
    return !stickerTypesWithoutSettings.includes(stickerType)
  }

  static getIcon(sticker: Tag): CustomIcons | undefined {
    const stickerType = this.getType(sticker)
    if (this.isSystem(sticker)) {
      return systemStickerIconsMap[stickerType as SystemStickerType]
    }
    return sticker.properties[stickerIconProperty] as CustomIcons
  }

  static setIcon(sticker: Tag, icon: CustomIcons | ''): void {
    sticker.properties[stickerIconProperty] = icon
    sticker.commit()
  }

  static isPinned(sticker: Tag, target: StickerHub): boolean {
    return sticker.id in target.stickers
  }

  static getUserSticker(obj: {companyId: DataObjectId}): Tag {
    const box = Sync.get(obj)
    return stickerCtl.User.getSticker(box)
  }

  static getDeadline(task: {
    id: DataObjectId
    stickers: {[id: string]: JSONLikeValue}
  }): DeadlineStickerValue {
    const box = Sync.getCurrentSandbox()
    const ctl = systemStickerCtl.Deadline
    const sticker = ctl.getSticker(box)
    const draftDeadline = GanttDraft.getDraft(task.id)?.deadline
    return (
      draftDeadline ?? (task.stickers?.[sticker.id] as DeadlineStickerValue)
    )
  }

  static getUsers(task: StickerHub, userSticker?: Tag): DataObjectId[] {
    const userStickerId = userSticker
      ? userSticker.id
      : this.getUserSticker(task).id
    const taskHubOrFallback =
      task instanceof Hub ? task : Sync.get(task).getHub(task.id) ?? task
    const value = taskHubOrFallback.stickers?.[userStickerId]
    if (!value) {
      return []
    }
    if (value instanceof Array) {
      return value
    } else {
      return [value]
    }
  }

  static setUsers(
    task: Hub,
    users: DataObjectId[],
    container: StickerContainerKey
  ): void {
    const box = Sync.get(task)
    if (!box) {
      logger.log('Sandbox not found')
      return
    }
    const sticker = stickerCtl.User.getSticker(box)
    if (users.length === 1) {
      Sticker.pin(sticker, container, task, users[0])
    } else if (users.length === 0) {
      Sticker.unpin(sticker, task)
    } else {
      Sticker.pin(sticker, container, task, users)
    }
  }

  static toggleUser(
    task: Hub,
    userId: DataObjectId,
    container: StickerContainerKey
  ): void {
    const box = Sync.get(task)
    if (!box) {
      logger.log('Sandbox not found')
      return
    }
    const sticker = stickerCtl.User.getSticker(box)
    const oldValue = task.stickers[sticker.id]
    const uniq = (arr: DataObjectId[]): DataObjectId[] =>
      ArrUtil.unique(arr, a => a)
    if (oldValue) {
      if (oldValue instanceof Array) {
        const ix = oldValue.indexOf(userId)
        if (ix > -1) {
          const newVal = [...oldValue.slice(0, ix), ...oldValue.slice(ix + 1)]
          // eslint-disable-next-line max-depth
          if (newVal.length === 0) {
            Sticker.unpin(sticker, task)
          } else if (newVal.length === 1) {
            Sticker.pin(sticker, container, task, newVal[0])
            Task.addAssignedBy(task, globals.currentUserId)
          } else {
            Sticker.pin(sticker, container, task, uniq(newVal))
            Task.addAssignedBy(task, globals.currentUserId)
          }
        } else {
          if (oldValue.length === 0) {
            Sticker.pin(sticker, container, task, userId)
          } else {
            Sticker.pin(sticker, container, task, [...oldValue, userId])
          }
          Task.addAssignedBy(task, globals.currentUserId)
        }
      } else {
        if (oldValue !== userId) {
          Sticker.pin(sticker, container, task, [oldValue, userId])
          Task.addAssignedBy(task, globals.currentUserId)
        } else {
          Sticker.unpin(sticker, task)
        }
      }
    } else {
      Sticker.pin(sticker, container, task, userId)
      Task.addAssignedBy(task, globals.currentUserId)
    }
  }

  static pin(
    sticker: Tag,
    container: StickerContainerKey,
    target: Hub,
    value?: any
  ): void {
    const controller = Sticker.getController(sticker)
    if (!controller?.onBeforePin(target, sticker, value)) {
      return
    }
    if (value === undefined && Sticker.isPinned(sticker, target)) {
      logger.log('Already pinned, skipping')
      return
    }
    if (
      !Stickers.onPin(Items.fromHub(target), Stickers.fromTag(sticker), value)
    ) {
      return
    }

    target.stickers[sticker.id] = value === undefined ? '' : value

    const isStopwatch = sticker.properties.stickerType === 'stopwatch'

    if ((isStopwatch && value) || !isStopwatch) {
      Task.commitWithCheckPhantom(target)
    }

    const isDeadline = sticker.properties.stickerType === 'deadline'
    if (isDeadline) {
      GanttDraft.removeDraft(target.id)
      GanttDraft.planDependentTasks(target.id)
    }

    controller.onPin(target, sticker, value)
    if (!value) {
      Sticker.pinEmptyHook(target, sticker, container)
    }
    if (isDeadline && value) {
      Items.changeDeadline(Items.fromHub(target))
    }
  }

  static unpin(sticker: Tag, target: Hub): void {
    if (!Stickers.onUnpin(Items.fromHub(target), Stickers.fromTag(sticker))) {
      return
    }

    if (sticker.title === 'Timetracking') {
      Tracking.send(TrackingEvent.timetrackingUnpin)
    }

    delete target.stickers[sticker.id]
    Task.commitWithCheckPhantom(target)

    const isDeadline = sticker.properties.stickerType === 'deadline'
    if (isDeadline) {
      GanttDraft.removeDraft(target.id)
      GanttDraft.planDependentTasks(target.id)

      Items.changeDeadline(Items.fromHub(target))
    }
  }

  static check(sticker: Tag): boolean {
    if (!sticker) {
      return false
    }
    const data = stickerDataFromTag(sticker)
    if (data?.deleted) {
      return false
    }
    if (data?.isSystem) {
      const ctl = systemStickerCtl[data.title]
      if (!ctl) {
        return false
      }
      const box = Sync.get(sticker)
      const shouldBe = ctl.getSticker(box)
      if (!shouldBe || shouldBe.id !== sticker.id) {
        return false
      }
    }
    return true
  }

  static getAllPinned(
    task: StickerHub,
    stickerBoardId?: DataObjectId
  ): Tag[] | null {
    const box = Sync.get(task)
    const isSubtask = SubtaskUtil.getParentTaskIds(task.id).some(
      id => !box.isDeleted(id)
    )
    let boardId = stickerBoardId

    if (!boardId) {
      const columns = isSubtask
        ? SubtaskUtil.getSubtaskColumnIds(task.id)
        : [task.location]

      if (!columns?.length) {
        return null
      }

      // TODO! it's incorrect to take first column. Columns actually can be from different boards.
      // so, board id should come in arguments to getAllPinned
      const column = box.getHub(columns[0])

      if (!column) {
        return null
      }

      boardId = column.location
    }

    const board = box.getHub(boardId)
    const ids = Object.keys(task.stickers ?? {}) as DataObjectId[]
    stickerCtl.Timetracking.enrichWithTimetrackingIfNeeded(task, ids, boardId)

    const allPinned = ids
      .map(k => box.get(k) as Tag)
      .filter(s => {
        return Sticker.check(s)
      })
    if (!board?.data) {
      return null
    }
    return Sticker.orderStickers(board.data, allPinned)
  }

  static state(
    sticker: Tag,
    taskLike: HubLike
  ): GenericStickerState | undefined {
    const box = Sync.get(taskLike)
    const task = box.get(taskLike.id) as Hub
    if (!task) {
      return undefined
    }
    const stickerData = task.stickers[sticker.id]
    const type = Sticker.getType(sticker)
    const isUser = type === 'user'
    const isDataSticker =
      type === 'deadline' ||
      type === 'timer' ||
      type === 'stopwatch' ||
      type === 'timetracking' ||
      type === 'repeat'

    if (isDataSticker || (isUser && box.isUserInCompany(stickerData))) {
      return stickerData
    }

    return sticker.states.index[stickerData] as GenericStickerState
  }

  static stateAsUser(
    state: GenericStickerState,
    type: StickerType,
    sticker: Tag
  ): false | User | undefined {
    return (
      type === 'user' &&
      Sync.get(sticker)
        .getAllUsers(false)
        .find(u => u.id === state)
    )
  }

  static stateAsText(
    state: GenericStickerState,
    type: StickerType,
    userName?: string
  ): string {
    const deadline = (state as DeadlineStickerValue).current
    const time = (state as TimerStickerValue).current
    const tracking = state as TimetrackerValue
    const sprint = state as SprintStickerState

    switch (type) {
      case 'user':
        return userName || (state as DataObjectId)
      case 'string':
        return (state as StringStickerState).name
      case 'sprint':
        return SprintCtl.formatSprint(sprint)
      case 'deadline':
        return DeadlineCtl.formatDeadline(deadline)
      case 'timer':
        return stickerCtl.Timer.formatTime(time)
      case 'stopwatch':
        return stickerCtl.Stopwatch.formatTime(time)
      case 'timetracking':
        return TimetrackingCtl.formatValue(tracking)
      case 'repeat':
        return Lang.msg.repeat.stickerName + '!'
      default:
        throw new TypeError(`Unknown sticker type ${type}`)
    }
  }

  static stateId(state: GenericStickerState): StickerStateId | DataObjectId {
    const {id} = state as {id: StickerStateId}
    return id || (state as DataObjectId)
  }

  static defaultStateColor(): string {
    return Sticker.defaultColor
  }

  static color(main: string): StickerColor {
    const {borderColor, backgroundColor, textColor} = Sticker

    return {
      main,
      border: borderColor(main),
      background: backgroundColor(main),
      text: textColor(main)
    }
  }

  /**
   * WARNING!
   * Old, deprecated stuff.
   */

  static borderColor(color: string): string {
    return color
  }

  static backgroundColor(color: string): string {
    return Util.mixColors('#ffffff', color, 0.8)
  }

  static textColor(color: string): string {
    return Util.mixColors('#000000', color, 0.4)
  }

  static textColorInSettings(color: AnyColor): AnyColor {
    return color
  }

  static backgroundColorDefault(color: string): string {
    return `rgba(${Util.colorToRGB(color).join(',')},0.16)`
  }

  static backgroundColorHover(color: string): string {
    return `rgba(${Util.colorToRGB(color).join(',')},0.1)`
  }

  static backgroundColorActive(color: string): string {
    return `rgba(${Util.colorToRGB(color).join(',')},0.32)`
  }
}
